import { MatchDetails } from "@/types/match";
import { format, isValid, parseISO } from 'date-fns';
import { ja } from 'date-fns/locale';
import Link from 'next/link';
import Image from 'next/image';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';

function resolveRgb(input: string): { r: number; g: number; b: number } | null {
  const raw = (input || '').trim();
  if (!raw) return null;

  const hex = raw.replace('#', '');
  if (/^[0-9a-fA-F]{3}$/.test(hex)) {
    const r = parseInt(hex[0] + hex[0], 16);
    const g = parseInt(hex[1] + hex[1], 16);
    const b = parseInt(hex[2] + hex[2], 16);
    return { r, g, b };
  }
  if (/^[0-9a-fA-F]{6}$/.test(hex)) {
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return { r, g, b };
  }

  const m = raw.match(/rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})/i);
  if (m?.[1] && m?.[2] && m?.[3]) {
    const r = Math.max(0, Math.min(255, Number(m[1])));
    const g = Math.max(0, Math.min(255, Number(m[2])));
    const b = Math.max(0, Math.min(255, Number(m[3])));
    return { r, g, b };
  }
  return null;
}

function resolveReadableTextColor(background: string | null | undefined): string {
  const rgb = background ? resolveRgb(background) : null;
  if (!rgb) return '#ffffff';

  const srgb = [rgb.r, rgb.g, rgb.b].map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  const luminance = 0.2126 * srgb[0] + 0.7152 * srgb[1] + 0.0722 * srgb[2];
  return luminance > 0.5 ? '#000000' : '#ffffff';
}

function getMatchSortMs(m: { matchDate?: string | Date | { toDate?: () => Date }; matchTime?: string } | null | undefined): number {
  const md = m?.matchDate;
  let base: Date | null = null;

  const mdAny = md as any;
  if (mdAny?.toDate && typeof mdAny.toDate === 'function') {
    base = mdAny.toDate();
  } else if (md instanceof Date) {
    base = md;
  } else if (typeof md === 'string') {
    const raw = md.trim();
    if (!raw) return Number.POSITIVE_INFINITY;
    const normalized = raw
      .replace(/\//g, '-')
      .replace(/^(\d{4})-(\d{1,2})-(\d{1,2})$/, (_m, y, mo, da) => `${y}-${String(mo).padStart(2, '0')}-${String(da).padStart(2, '0')}`);
    const iso = parseISO(normalized);
    base = isValid(iso) ? iso : new Date(normalized);
  }
  const baseMs = base instanceof Date && !Number.isNaN(base.getTime()) ? base.getTime() : Number.POSITIVE_INFINITY;

  const rawTime = typeof m?.matchTime === 'string' ? m.matchTime.trim() : '';
  const tm = rawTime.match(/^\s*(\d{1,2}):(\d{2})\s*$/);
  if (!tm) return baseMs;
  const hh = Math.min(23, Math.max(0, Number(tm[1])));
  const mm = Math.min(59, Math.max(0, Number(tm[2])));
  return baseMs + (hh * 60 + mm) * 60 * 1000;
}

function UpcomingMatchesCarousel({
  matches,
  mainTeamId,
  backgroundColor,
  colorTheme = 'dark',
}: {
  matches: MatchDetails[];
  mainTeamId?: string | null;
  backgroundColor?: string | null;
  colorTheme?: 'dark' | 'light';
}) {
  const isDark = colorTheme === 'dark';
  const list = (Array.isArray(matches) ? matches : []).filter(Boolean).slice(0, 3);
  if (list.length === 0) return null;

  const badgeBg = backgroundColor || '#991b1b';
  const badgeText = resolveReadableTextColor(badgeBg);

  return (
    <div className="w-full">
      <Carousel className="w-full">
        <CarouselContent className="-ml-4">
          {list.map((m) => {
            const matchDate = new Date(m.matchDate);
            const dateLabel = Number.isFinite(matchDate.getTime())
              ? format(matchDate, 'yyyy年M月d日(EEE)', { locale: ja })
              : '';
            const timeLabel = m.matchTime ? String(m.matchTime) : '';

            const isHome = Boolean(mainTeamId && m.homeTeam === mainTeamId);
            const opponentName = isHome ? m.awayTeamName : m.homeTeamName;
            const opponentLogo = isHome ? m.awayTeamLogo : m.homeTeamLogo;
            const isPracticeMatch = (m.competitionName || '').includes('練習試合');
            const roundNameText = (m.roundName || '').toString().trim();
            const isSingleRound = m.roundId === 'single' || roundNameText === '単発';

            return (
              <CarouselItem key={m.id} className="pl-4 basis-full">
                <div className="w-full px-0 sm:px-2">
                  <div className={`w-full rounded-xl shadow-sm overflow-hidden border ${isDark ? 'bg-[#15161b] border-white/10 text-slate-100' : 'bg-gray-50 border-black/5 text-gray-900'}`}>
                    <div className="px-4 py-3 flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        {m.competitionLogoUrl ? (
                          <Image
                            src={m.competitionLogoUrl}
                            alt=""
                            width={28}
                            height={28}
                            className="h-7 w-7 object-contain"
                          />
                        ) : (
                          <div className="h-7 w-7" />
                        )}
                        <div className="min-w-0">
                          <div className={`text-xs font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>今後の試合</div>
                          <div className={`mt-0.5 text-sm font-semibold truncate ${isDark ? 'text-slate-400' : 'text-gray-700'}`}>
                            {dateLabel}{timeLabel ? ` ${timeLabel}` : ''}
                          </div>
                        </div>
                      </div>

                      <div
                        className="flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold"
                        style={{ backgroundColor: badgeBg, color: badgeText }}
                      >
                        {isHome ? 'H' : 'A'}
                      </div>
                    </div>

                    <div className={`border-t ${isDark ? 'border-white/10' : 'border-gray-100'}`} />

                    <div className="px-4 py-5 flex items-center gap-4">
                      {opponentLogo ? (
                        <Image
                          src={opponentLogo}
                          alt={opponentName}
                          width={72}
                          height={72}
                          className="h-[72px] w-[72px] rounded-full object-contain bg-white"
                        />
                      ) : (
                        <div className="h-[72px] w-[72px] rounded-full bg-gray-100" />
                      )}

                      <div className="min-w-0">
                        <div className={`text-[11px] font-semibold truncate ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                          {(m.competitionName || '').toString()}
                        </div>
                        <div className={`mt-0.5 text-xl font-black leading-tight truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {opponentName}
                        </div>
                        {!isPracticeMatch && !isSingleRound && roundNameText ? (
                          <div className={`mt-0.5 text-sm truncate ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
                            {roundNameText}
                          </div>
                        ) : null}
                      </div>
                    </div>

                    <div className="px-4 pb-4 flex justify-center">
                      <div className="rounded-full bg-red-600 text-white px-5 py-2 text-xs font-bold text-center opacity-40 grayscale cursor-not-allowed select-none">
                        チケット
                      </div>
                    </div>
                  </div>
                </div>
              </CarouselItem>
            );
          })}
        </CarouselContent>

        <CarouselPrevious className="absolute left-[-10px] sm:left-[-16px] top-1/2 -translate-y-1/2 z-10 bg-white/85 text-gray-900 border-none hover:bg-white" />
        <CarouselNext className="absolute right-[-10px] sm:right-[-16px] top-1/2 -translate-y-1/2 z-10 bg-white/85 text-gray-900 border-none hover:bg-white" />
      </Carousel>
    </div>
  );
}

function TeamDisplay({ logo, name }: { logo?: string, name: string }) {
  return (
    <div className="flex flex-col items-center space-y-2 text-center w-24 md:w-28">
      {logo ? (
        <Image src={logo} alt={name} width={48} height={48} className="w-10 h-10 md:w-12 md:h-12 rounded-full object-contain" />
      ) : (
        <div className="w-10 h-10 md:w-12 md:h-12 bg-gray-700 rounded-full flex items-center justify-center text-white font-bold text-lg md:text-xl">{name.charAt(0)}</div>
      )}
      <span className="font-bold text-xs sm:text-sm md:text-base truncate w-full">{name}</span>
    </div>
  );
}

function getRoundLabel(roundName?: string) {
  const s = (roundName || '').trim();
  if (!s) return '';
  const m = s.match(/(\d+)/);
  if (m?.[1]) return `MW ${m[1]}`;
  return s;
}

function RecentMatchesStrip({
  matches,
  mainTeamId,
  clubSlug,
  colorTheme = 'dark',
}: {
  matches: MatchDetails[];
  mainTeamId?: string | null;
  clubSlug: string;
  colorTheme?: 'dark' | 'light';
}) {
  const isDark = colorTheme === 'dark';
  const items = (matches || [])
    .filter((m) => m && typeof m === "object")
    .filter((m) => typeof m.scoreHome === 'number' && typeof m.scoreAway === 'number')
    .slice()
    .sort((a, b) => getMatchSortMs(a) - getMatchSortMs(b));
  if (items.length === 0) return null;

  const resolveIsHome = (m: MatchDetails) => {
    if (mainTeamId) return m.homeTeam === mainTeamId;
    return false;
  };

  const resolveOutcome = (m: MatchDetails) => {
    const isHome = resolveIsHome(m);
    const my = isHome ? (m.scoreHome ?? 0) : (m.scoreAway ?? 0);
    const opp = isHome ? (m.scoreAway ?? 0) : (m.scoreHome ?? 0);
    if (my > opp) return 'win' as const;
    if (my < opp) return 'loss' as const;
    return 'draw' as const;
  };

  const resolveOpponent = (m: MatchDetails) => {
    const isHome = resolveIsHome(m);
    return {
      competitionLogoUrl: m.competitionLogoUrl,
      logo: isHome ? m.awayTeamLogo : m.homeTeamLogo,
      name: isHome ? m.awayTeamName : m.homeTeamName,
      ha: isHome ? '(H)' : '(A)',
      scoreText: `${m.scoreHome ?? '-'}-${m.scoreAway ?? '-'}`,
      outcome: resolveOutcome(m),
      roundLabel:
        m.roundId === 'single' || (m.roundName || '').trim() === '単発'
          ? (m.competitionName || '')
          : getRoundLabel(m.roundName),
    };
  };

  const outcomeClass = (o: 'win' | 'loss' | 'draw') => {
    if (o === 'win') return 'bg-emerald-500 text-white ring-1 ring-emerald-200/70 shadow-[0_0_18px_rgba(16,185,129,0.28)]';
    if (o === 'loss') return 'bg-rose-500 text-white ring-1 ring-rose-200/70 shadow-[0_0_18px_rgba(244,63,94,0.28)]';
    return 'bg-slate-500 text-white ring-1 ring-slate-200/60 shadow-[0_0_18px_rgba(148,163,184,0.18)]';
  };

  return (
    <div className="mb-4">
      <div className="flex gap-3 overflow-x-auto pb-2 md:grid md:grid-flow-col md:auto-cols-fr">
        {items.map((m) => {
          const opp = resolveOpponent(m);
          const href = `/${clubSlug}/matches/${m.competitionId}/${m.roundId}/${m.id}`;
          return (
            <Link key={m.id} href={href} className="flex-shrink-0 w-[146px] md:w-full md:min-w-[110px]">
              <div className={`rounded-xl border p-3 transition-colors ${isDark ? 'border-white/10 bg-[#15161b] hover:bg-white/10' : 'border-black/10 bg-white hover:bg-muted/50'}`}>
                <div className={`mb-2 flex items-center justify-center gap-1 text-[11px] font-semibold ${isDark ? 'text-slate-500' : 'text-muted-foreground'}`}>
                  {opp.competitionLogoUrl ? (
                    <Image
                      src={opp.competitionLogoUrl}
                      alt=""
                      width={12}
                      height={12}
                      className="h-3 w-3 object-contain"
                    />
                  ) : null}
                  <span>{opp.roundLabel}</span>
                </div>
                <div className="flex flex-col items-center gap-2">
                  {opp.logo ? (
                    <Image src={opp.logo} alt={opp.name} width={44} height={44} className="w-11 h-11 rounded-full object-contain" />
                  ) : (
                    <div className={`w-11 h-11 rounded-full flex items-center justify-center font-bold text-lg ${isDark ? 'bg-white/10 text-white' : 'bg-gray-700 text-white'}`}>
                      {opp.name.charAt(0)}
                    </div>
                  )}
                  <div className={`text-[12px] font-semibold leading-tight text-center ${isDark ? 'text-slate-100' : 'text-gray-900'}`}>
                    <div className="truncate max-w-[118px]">{opp.name}</div>
                    <div className={`text-[10px] ${isDark ? 'text-slate-500' : 'text-muted-foreground'}`}>{opp.ha}</div>
                  </div>
                  <div className={"w-full rounded-lg px-2 py-1 text-center text-xs font-black " + outcomeClass(opp.outcome)}>
                    {opp.scoreText}
                  </div>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

export function MatchResultsList({
  matches,
  clubSlug,
  rounds,
  selectedRoundIndex,
  onRoundChange,
  colorTheme = 'dark',
}: {
  matches: MatchDetails[];
  clubSlug: string;
  rounds: { roundId: string; roundName: string }[];
  selectedRoundIndex: number;
  onRoundChange: (index: number) => void;
  colorTheme?: 'dark' | 'light';
}) {
  const isDark = colorTheme === 'dark';
  const items = (matches || [])
    .filter((m) => m && typeof m === "object")
    .filter((m) => typeof m.scoreHome === 'number' && typeof m.scoreAway === 'number')
    .slice()
    .sort((a, b) => getMatchSortMs(b) - getMatchSortMs(a));

  if (items.length === 0 || rounds.length === 0) {
    return (
      <div className={`p-4 rounded-2xl text-center shadow-sm border ${isDark ? 'bg-[#101116] text-slate-400 border-white/10' : 'bg-white text-muted-foreground border-black/10'}`}>
        <p>表示できる試合結果がありません。</p>
      </div>
    );
  }

  const currentRound = rounds[selectedRoundIndex];
  if (!currentRound) {
    return (
      <div className={`p-4 rounded-2xl text-center shadow-sm border ${isDark ? 'bg-[#101116] text-slate-400 border-white/10' : 'bg-white text-muted-foreground border-black/10'}`}>
        <p>表示できる試合結果がありません。</p>
      </div>
    );
  }
  const currentRoundMatches = items
    .filter((m) => m.roundName === currentRound.roundName)
    .sort((a, b) => getMatchSortMs(a) - getMatchSortMs(b));
  const competitionName = currentRoundMatches[0]?.competitionName;
  const competitionLogoUrl = currentRoundMatches[0]?.competitionLogoUrl;

  const dateLabel = (value: string) => {
    const d = new Date(value);
    if (!Number.isFinite(d.getTime())) return '日付未定';
    return format(d, 'yyyy年M月d日EEE曜日', { locale: ja });
  };

  const grouped = currentRoundMatches.reduce((acc: Map<string, MatchDetails[]>, match: MatchDetails) => {
    const key = dateLabel(match.matchDate);
    if (!acc.has(key)) acc.set(key, []);
    acc.get(key)!.push(match);
    return acc;
  }, new Map<string, MatchDetails[]>());

  const handlePrevRound = () => {
    if (selectedRoundIndex > 0) onRoundChange(selectedRoundIndex - 1);
  };

  const handleNextRound = () => {
    if (selectedRoundIndex < rounds.length - 1) onRoundChange(selectedRoundIndex + 1);
  };

  return (
    <div className={`rounded-2xl shadow-sm border overflow-hidden ${isDark ? 'bg-[#101116] text-slate-100 border-white/10' : 'bg-white text-gray-900 border-black/10'}`}>
      {competitionName && (
        <div className={`px-3 sm:px-4 py-2 border-b flex items-center gap-2 ${isDark ? 'border-white/10' : 'border-gray-100'}`}>
          {competitionLogoUrl && (
            <Image
              src={competitionLogoUrl}
              alt={competitionName}
              width={16}
              height={16}
              className="rounded-full object-contain"
            />
          )}
          <span className={`text-[10px] font-semibold ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>{competitionName}</span>
        </div>
      )}
      <div className="px-3 sm:px-4 py-3 flex items-center justify-between">
        <button
          type="button"
          onClick={handlePrevRound}
          disabled={selectedRoundIndex === 0}
          className={`h-8 w-8 rounded-full disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center ${isDark ? 'bg-white/10 text-slate-200 hover:bg-white/15' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
        </button>
        <div className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
          {currentRound.roundName === '単発'
            ? (competitionName || '練習試合')
            : (currentRound.roundName || '節')}
        </div>
        <button
          type="button"
          onClick={handleNextRound}
          disabled={selectedRoundIndex === rounds.length - 1}
          className={`h-8 w-8 rounded-full disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center ${isDark ? 'bg-white/10 text-slate-200 hover:bg-white/15' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        </button>
      </div>
      <div className="p-3 sm:p-4 space-y-3">
        {Array.from(grouped.entries()).map(([date, dateMatches]) => (
          <div key={date}>
            <div className={`mb-1 rounded-xl px-4 py-3 text-sm font-black ${isDark ? 'bg-white/10 text-white' : 'bg-gray-100 text-gray-950'}`}>
              {date}
            </div>
            <div className={isDark ? 'divide-y divide-white/10' : 'divide-y divide-gray-100'}>
              {dateMatches.map((match) => {
                const href = `/${clubSlug}/matches/${match.competitionId}/${match.roundId}/${match.id}`;
                return (
                  <Link key={match.id} href={href} className={`grid grid-cols-[34px_1fr_auto_1fr] items-center gap-2 py-3 transition-colors ${isDark ? 'hover:bg-white/5' : 'hover:bg-gray-50'}`}>
                    <div className="flex justify-center">
                      <span className={`rounded-full px-2 py-1 text-[11px] font-black ${isDark ? 'bg-white/10 text-slate-500' : 'bg-gray-100 text-gray-400'}`}>FT</span>
                    </div>
                    <div className="flex min-w-0 items-center justify-end gap-2 text-right">
                      <span className={`truncate text-xs font-bold leading-tight sm:text-sm ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>{match.homeTeamName}</span>
                      {match.homeTeamLogo ? (
                        <Image src={match.homeTeamLogo} alt={match.homeTeamName} width={26} height={26} className="h-6 w-6 flex-shrink-0 object-contain" />
                      ) : (
                        <div className={`h-6 w-6 flex-shrink-0 rounded-full ${isDark ? 'bg-white/10' : 'bg-gray-100'}`} />
                      )}
                    </div>
                    <div className={`min-w-[54px] text-center text-base font-black tabular-nums sm:text-lg ${isDark ? 'text-white' : 'text-gray-950'}`}>
                      {match.scoreHome ?? '-'} - {match.scoreAway ?? '-'}
                    </div>
                    <div className="flex min-w-0 items-center justify-start gap-2">
                      {match.awayTeamLogo ? (
                        <Image src={match.awayTeamLogo} alt={match.awayTeamName} width={26} height={26} className="h-6 w-6 flex-shrink-0 object-contain" />
                      ) : (
                        <div className={`h-6 w-6 flex-shrink-0 rounded-full ${isDark ? 'bg-white/10' : 'bg-gray-100'}`} />
                      )}
                      <span className={`truncate text-xs font-bold leading-tight sm:text-sm ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>{match.awayTeamName}</span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function NextMatch({ match, colorTheme = 'dark' }: { match: MatchDetails | null; colorTheme?: 'dark' | 'light' }) {
  const isDark = colorTheme === 'dark';
  if (!match) return <div className="text-center p-8">No upcoming matches.</div>;

  const matchDate = new Date(match.matchDate);

  return (
    <div className={`rounded-xl border p-4 md:p-6 flex flex-col items-center shadow-sm transition-colors ${isDark ? 'bg-[#15161b] text-slate-100 border-white/10 hover:bg-white/10' : 'bg-white text-gray-900 border-black/10 hover:shadow-xl'}`}>
      <div className="w-full flex justify-around items-center">
        <TeamDisplay logo={match.homeTeamLogo} name={match.homeTeamName} />
        <div className="text-center px-1">
          <p className="text-xs sm:text-sm md:text-base text-muted-foreground">{match.matchTime ? `${format(matchDate, 'M/d')} ${match.matchTime}` : format(matchDate, 'M/d')}</p>
          <p className="text-base sm:text-lg md:text-xl font-bold">KICK OFF</p>
          <div className="mt-2 space-y-0.5">
            {match.competitionLogoUrl ? (
              <div className="flex items-center justify-center">
                <Image
                  src={match.competitionLogoUrl}
                  alt=""
                  width={14}
                  height={14}
                  className="h-3.5 w-3.5 object-contain"
                />
              </div>
            ) : (
              <p className="text-[10px] sm:text-xs text-muted-foreground truncate">{match.competitionName || ''}</p>
            )}
            {match.roundId !== 'single' && (match.roundName || '').trim() ? (
              <p className="text-[10px] sm:text-xs text-muted-foreground truncate">{match.roundName || ''}</p>
            ) : null}
          </div>
        </div>
        <TeamDisplay logo={match.awayTeamLogo} name={match.awayTeamName} />
      </div>
    </div>
  );
}

interface MatchSectionProps {
  nextMatch: MatchDetails | null;
  upcomingMatches?: MatchDetails[];
  recentMatches?: MatchDetails[];
  mainTeamId?: string | null;
  clubSlug: string;
  backgroundColor?: string | null;
  colorTheme?: 'dark' | 'light';
}

export function MatchSection({
  nextMatch,
  upcomingMatches = [],
  recentMatches = [],
  mainTeamId,
  clubSlug,
  backgroundColor,
  colorTheme = 'dark',
}: MatchSectionProps) {
  const isDark = colorTheme === 'dark';
  const upcoming = (Array.isArray(upcomingMatches) ? upcomingMatches : []).slice(0, 3);
  const hasRecentResults = (Array.isArray(recentMatches) ? recentMatches : []).some(
    (m) => m && typeof m.scoreHome === 'number' && typeof m.scoreAway === 'number'
  );
  return (
    <section className="pt-0 pb-8 md:pb-12">
      <div className={`rounded-2xl shadow-sm p-4 md:p-6 w-full lg:min-h-[520px] flex flex-col border ${isDark ? 'bg-[#101116] border-white/10 text-slate-100' : 'bg-gray-100 border-black/5 text-gray-900'}`}>
        <h2 className={`text-2xl md:text-3xl font-bold mb-4 text-center ${isDark ? 'text-white' : 'text-gray-900'}`}>MATCHES</h2>
        <RecentMatchesStrip matches={recentMatches} mainTeamId={mainTeamId} clubSlug={clubSlug} colorTheme={colorTheme} />
        <div className="mt-4">
          {upcoming.length > 0 ? (
            <UpcomingMatchesCarousel matches={upcoming} mainTeamId={mainTeamId} backgroundColor={backgroundColor} colorTheme={colorTheme} />
          ) : nextMatch || !hasRecentResults ? (
            <NextMatch match={nextMatch} colorTheme={colorTheme} />
          ) : null}
        </div>
      </div>
    </section>
  );
}
